import React, { useEffect, useMemo, useState } from "react";
import { Currency } from "../../../_app/types";
import { Strategy, TimeFrame } from "../../../store/slices/strategySlice";
import { IconButton, InputAdornment, Paper, Slider, Stack, Tab, Typography } from "@mui/material";
import { TabContext } from "@mui/lab";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { precision } from "../../../utils/_precision";
import { _roundDec } from "../../../utils/_roundToDecimals";
import formatNumber from "../../../utils/formatNumber";
import { IconButtonStyled, TIME_FRAME_PRESETS } from "../../../_app/consts";
import { obtainTimeframeProfitPercent } from "../../../utils/utils";
import {TabsStyled} from "../../../_components/TabsStyled";
import SimpleFlex from "../../../_components/SimpleFlex";
import {TextFieldStyled} from "../../../_components/TextFieldStyled";
import {NumericFormatCustom} from "../../../components/ui/StyledAdron";
import IconComponent from "../../../_components/IconComponent";
import AdronTypography from "../../../_components/AdronTypography";
import {MinusIcon, PlusIcon} from "../../../components/ui/icons/adronIcons";
import DottedTextField from "../../../_components/DottedTextField";

interface Props {
  strategy: Strategy;
  middlePrice: number;
  onChange: (value: number) => void;
  onChangeCurrency: (value: Currency) => void;
  onChangeStepsCount: (value: number) => void;
  timeTerm: "Short" | "Mid" | "Long";
  timeFrames: TimeFrame[];
}

export default function AutoStrategyInvestment({
  strategy,
  onChange,
  onChangeCurrency,
  middlePrice,
  timeTerm,
  timeFrames,
}: Props) {
  const theme = useTheme();

  const CustomGridMode = useSelector((state: RootState) => state.customGridMode);
  const [currency, setCurrency] = useState<string>(
    strategy.currencyType === "BASE" ? strategy.base : strategy.quote
  );

  const stepInvestment = useMemo(() => {
    return currency === strategy.base ? strategy.ticker.priceStep : 1;
  }, [currency]);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setCurrency(newValue);
    if (newValue === strategy.base) {
      onChangeCurrency("BASE");
      const convertInvestment = _roundDec(
        strategy.investment / middlePrice,
        precision(strategy.ticker.orderStep)
      );
      setInvestmentString(String(convertInvestment));
      onChange(convertInvestment);
    }
    if (newValue === strategy.quote) {
      onChangeCurrency("QUOTE");
      const convertInvestment = _roundDec(
        strategy.investment * middlePrice,
        precision(strategy.ticker.orderStep)
      );
      setInvestmentString(String(convertInvestment));
      onChange(convertInvestment);
    }
  };
  const handleIncreaseStep = () => {
    let newValue = strategy.investment + stepInvestment;
    onChange(newValue);
    setInvestmentString(String(newValue));
  };
  const handleDecreaseStep = () => {
    let newValue = strategy.investment - stepInvestment;
    onChange(newValue);
    setInvestmentString(String(newValue));
  };

  const minInvestmentQuote = useMemo(() => {
    let timeframesArr = [];
    if (strategy.customFrame) {
      timeframesArr = timeFrames
        .filter((frame) => frame.checked)
        .map(({ checked, ...rest }) => rest);
    } else {
      timeframesArr = TIME_FRAME_PRESETS[timeTerm]
        .filter((frame) => frame.checked)
        .map(({ checked, ...rest }) => rest);
    }

    let timeframeRequiredMultipliers: number[] = timeframesArr.flatMap((timeframe) => {
      let timeframeRequiredRatio =
        strategy.ticker.orderStep / obtainTimeframeProfitPercent(timeframe.interval);
      return [timeframeRequiredRatio / timeframe.assignedVolume];
    });
    console.log(timeframeRequiredMultipliers);
    let biggestTimeframeRequiredMultiplier = Math.max(...timeframeRequiredMultipliers);
    let range = Math.abs(strategy.maxPrice - strategy.minPrice);

    return _roundDec(range * biggestTimeframeRequiredMultiplier, 2);
  }, [strategy.customFrame, strategy.minPrice, strategy.maxPrice, timeTerm, timeFrames]);

  const minInvestmentCurrency = useMemo(() => {
    return currency === strategy.base
      ? _roundDec(minInvestmentQuote / middlePrice, precision(strategy.ticker.orderStep))
      : minInvestmentQuote;
  }, [minInvestmentQuote, currency]);

  const helperTextInvestment = useMemo(() => {
    return strategy.investment < minInvestmentCurrency
      ? `Minimum allowed value is ${minInvestmentCurrency} ${currency}`
      : "";
  }, [minInvestmentCurrency, strategy.investment]);

  // TODO
  // эта штука называется timeframeProfitPercent
  // нам надо считать такую вещь
  // timeframeRequredRatio = ticker.orderStep / timeframeProfitPercent
  // потом, для каждого таймфрейма, который выбрал юзер, расчитываем
  // timeframeRequiredMultipler = timeframeRequredRatio * timeframeSetting.AssignedVolume
  // (AsignedVolume это вот хрень, которую юзер в адвансед выбирает - 30% или там 20% или мы за него проставляем при выборе short-term,long-term).
  // Далее мы выбираем самый большой timeframeRequiredMultipler среди всех таймфреймов, которые активные и кладем его в biggestTimeframeRequiredMultipler
  // Это все при создании стратегии
  // дальше мы считаем strategyPriceRange = maxPrice - minPrice
  // и наконец, наконец, мы считаем minInvestmentQuote = strategyPriceRange * biggestTimeframeRequiredMultipler
  // Это будет как раз минимальные инвестиции, именно инвестиции, не маржа

  const [investmentString, setInvestmentString] = useState(String(strategy.investment));

  const handleChangeInvestment = (value: string) => {
    setInvestmentString(value);
    onChange(Number(value));
  };

  const handleInvestmentSlider = (
    event: React.SyntheticEvent | Event,
    newValue: number | number[]
  ) => {
    setInvestmentString(String(newValue as number));
    onChange(newValue as number);
  };

  useEffect(() => {
    console.log(timeFrames);
  }, [timeFrames]);

  return (
    <Paper
      elevation={0}
      className="base-adron-paper paper-light"
      style={{ backgroundColor: theme.palette.grey[400] }}
    >
      <Stack direction={"column"} spacing={2}>
        <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
          <Typography style={{ opacity: "50%" }} variant="body2" color="textSecondary">
            Investment
          </Typography>
          <TabContext value={currency}>
            <TabsStyled style={{ borderRadius: 12 }} onChange={handleChange}>
              <Tab
                disabled={CustomGridMode}
                label={strategy.base}
                value={strategy.base}
                className="AdronSecondaryTabs"
              />
              <Tab
                disabled={CustomGridMode}
                label={strategy.quote}
                value={strategy.quote}
                className="AdronSecondaryTabs"
              />
            </TabsStyled>
          </TabContext>
        </Stack>
        <SimpleFlex gap={16}>
          <TextFieldStyled
            disabled={CustomGridMode}
            sx={{ textAlign: "right" }}
            fullWidth={true}
            label=""
            variant="outlined"
            value={investmentString}
            error={strategy.investment < minInvestmentCurrency}
            helperText={helperTextInvestment}
            size="small"
            onChange={(e) => handleChangeInvestment(e.target.value)}
            InputProps={{
              inputComponent: NumericFormatCustom as any,
              startAdornment: (
                <InputAdornment position="start">
                  <IconComponent iconName={`${currency}`} size={18} />
                </InputAdornment>
              ),
              inputProps: {
                inputMode: "decimal",
                pattern: "[0-9]*",
                decimalCustom:
                  currency === strategy.base
                    ? precision(strategy.ticker.priceStep * strategy.ticker.orderStep)
                    : 2,
              },
              endAdornment: (
                <InputAdornment position="end">
                  <AdronTypography halfOpacity value={`${currency}`} />
                </InputAdornment>
              ),
            }}
            required
          />
          <SimpleFlex gap={8}>
            <IconButton sx={IconButtonStyled(theme)} onClick={handleDecreaseStep}>
              <MinusIcon />
            </IconButton>
            <IconButton sx={IconButtonStyled(theme)} onClick={handleIncreaseStep}>
              <PlusIcon />
            </IconButton>
          </SimpleFlex>
        </SimpleFlex>
        <Stack direction={"column"} justifyContent={"center"} spacing={"2px"}>
          <DottedTextField
            placeholder={`Required margin`}
            value={`$${formatNumber(
              _roundDec(
                (currency === strategy.base
                  ? strategy.investment * middlePrice
                  : strategy.investment) / strategy.leverage,
                2
              )
            )}`}
          />
        </Stack>
      </Stack>
      <Slider
        disabled={CustomGridMode}
        size="medium"
        value={Number(investmentString)}
        max={currency === strategy.base ? 100000 / middlePrice : 100000} // Current Account Balance
        min={minInvestmentCurrency}
        step={stepInvestment}
        onChange={handleInvestmentSlider}
        onChangeCommitted={handleInvestmentSlider}
        valueLabelDisplay="auto"
      />
    </Paper>
  );
}
